import { useLearningManagementServiceV1 } from "@rw1/api-services";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  toApiLearningExternalMetricFilter,
  toLicenseRatingSeries,
} from "../../../../domain/mappers/metricsMappers";
import { GC_TIME_MS, STALE_TIME_MS } from "../../../../hooks/constants";
import type {
  ExperienceFeedbackRating,
  FeedbackOverallRating,
} from "../../InternalUsageTab/types";
import {
  computeOverallRating,
  extractRatingValues,
} from "../../utils/ratingUtils";

export const EXTERNAL_FEEDBACK_RATINGS_QUERY_KEY = "externalFeedbackRatings";

interface SelectedLicense {
  id: string;
  label: string;
}

export interface UseExternalFeedbackRatingsParams {
  selectedLicenses: SelectedLicense[];
  startDate: Date;
  endDate: Date;
}

export interface UseExternalFeedbackRatingsResult {
  feedbackData: ExperienceFeedbackRating[];
  overallRating: FeedbackOverallRating;
  isLoading: boolean;
  isFetching: boolean;
}

export const useExternalFeedbackRatings = ({
  selectedLicenses,
  startDate,
  endDate,
}: UseExternalFeedbackRatingsParams): UseExternalFeedbackRatingsResult => {
  const { companyId } = useParams();
  const learningService = useLearningManagementServiceV1();

  const licenseIds = useMemo(
    () => selectedLicenses.map((l) => l.id),
    [selectedLicenses],
  );

  const { data, isLoading, isFetching } = useQuery({
      queryKey: [
        EXTERNAL_FEEDBACK_RATINGS_QUERY_KEY,
        companyId,
        startDate.toISOString(),
        endDate.toISOString(),
        licenseIds,
      ],
      queryFn: async () => {
        const filter = toApiLearningExternalMetricFilter({
          startDate,
          endDate,
          licenseIds,
        });

        const result = await learningService.getExternalCompanyRatings(
          companyId!,
          filter,
        );

        return toLicenseRatingSeries(result);
      },
      enabled: !!companyId && licenseIds.length > 0,
      placeholderData: keepPreviousData,
      staleTime: STALE_TIME_MS,
      gcTime: GC_TIME_MS,
      retry: 1,
    });

  const overallRating = useMemo(
    () => computeOverallRating(data?.aggregateValues),
    [data],
  );

  const feedbackData = useMemo<ExperienceFeedbackRating[]>(() => {
    const dataPointLookup = new Map(
      data?.licenses.map((rating) => [rating.licenseId, rating]) ?? [],
    );

    return selectedLicenses.map((license) => ({
      experienceId: license.id,
      experienceName: license.label,
      ...extractRatingValues(dataPointLookup.get(license.id)?.values),
    }));
  }, [data, selectedLicenses]);

  return {
    feedbackData,
    overallRating,
    isLoading,
    isFetching,
  };
};
